import { DialogCloseEvent, DialogComponent, DialogOpenEvent, lockPageScroll, unlockPageScroll } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { removeTrapFocus, trapFocus } from '@theme/focus';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog', 'overlay', 'trigger'];

  /** @type {HTMLElement | null} */
  #previouslyFocused = null;

  /** @type {number} */
  #openAnimationFrame = 0;

  /** @type {number} */
  #focusAnimationFrame = 0;

  /** @type {boolean} */
  #isDrawerEnabled = true;

  connectedCallback() {
    super.connectedCallback();
    this.#isDrawerEnabled = this.dataset.drawerEnabled === 'true';
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.refs.overlay.addEventListener('click', this.closeDialog);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.refs.overlay.removeEventListener('click', this.closeDialog);
    document.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener('pointerdown', this.#handlePointerDown, true);
    // If the drawer is removed while open, ensure focus-trap listeners are cleaned up.
    if (this.refs.dialog?.open) removeTrapFocus(this);
    this.#cancelPendingFrames();
    // Ensure scroll is restored if the drawer is removed while open.
    if (this.refs.dialog?.open) unlockPageScroll();
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  /**
   * Open drawer (custom overlay + focus trap).
   * We intentionally avoid `dialog.showModal()` here because a stuck/inert top-layer/backdrop
   * can make the drawer appear but block all interaction.
   */
  showDialog() {
    // Prevent opening if drawer is disabled
    if (!this.#isDrawerEnabled) return;
    
    const { dialog, overlay } = this.refs;
    if (dialog.open) return;

    this.#previouslyFocused = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    // Lock scroll while keeping scroll position stable.
    // Also compensate for scrollbar removal to prevent initial horizontal layout shift.
    lockPageScroll();

    // Show overlay + drawer
    // Important: remove `hidden` first so the overlay can render at opacity: 0,
    // then set `data-open="true"` on the next frame so the CSS transition can
    // animate opacity -> 1.
    overlay.hidden = false;
    this.#cancelPendingFrames();
    this.#openAnimationFrame = requestAnimationFrame(() => {
      // If the drawer was closed before this frame runs, avoid leaving stale state behind.
      if (!this.refs.dialog.open || this.refs.overlay.hidden) return;
      this.dataset.open = 'true';
    });

    if (typeof dialog.show === 'function') {
      dialog.show(); // non-modal; overlay provides click-blocking
    } else {
      dialog.setAttribute('open', '');
    }

    document.addEventListener('keydown', this.#handleKeyDown);
    document.addEventListener('pointerdown', this.#handlePointerDown, true);

    // Trap focus inside the drawer for accessibility
    trapFocus(dialog, this);

    // Prefer focusing the explicit close button if present
    const closeButton = /** @type {HTMLElement | undefined} */ (this.refs.closeButton);
    this.#focusAnimationFrame = requestAnimationFrame(() => {
      if (!this.refs.dialog.open) return;
      (closeButton || dialog).focus();
    });

    this.dispatchEvent(new DialogOpenEvent());
  }

  closeDialog = async () => {
    const { dialog, overlay } = this.refs;
    if (!dialog.open) return;

    this.#cancelPendingFrames();
    document.removeEventListener('keydown', this.#handleKeyDown);
    document.removeEventListener('pointerdown', this.#handlePointerDown, true);
    removeTrapFocus(this);

    // Hide overlay first so clicks don't get "stuck"
    overlay.hidden = true;
    delete this.dataset.open;

    // Restore scroll
    unlockPageScroll();

    dialog.close();

    // Restore focus to the trigger/previous element
    this.#previouslyFocused?.focus?.();

    this.dispatchEvent(new DialogCloseEvent());
  };

  /** @param {KeyboardEvent} event */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.refs.dialog.open) return;

    event.preventDefault();
    this.closeDialog();
  };

  /** @param {PointerEvent} event */
  #handlePointerDown = (event) => {
    if (!this.refs.dialog.open) return;
    if (!(event.target instanceof Node)) return;

    // Ignore clicks inside the drawer
    if (this.refs.dialog.contains(event.target)) return;

    // Ignore clicks on the cart icon trigger
    if (this.refs.trigger.contains(event.target)) return;

    // Anything else is considered "click outside"
    this.closeDialog();
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #cancelPendingFrames() {
    if (this.#openAnimationFrame) cancelAnimationFrame(this.#openAnimationFrame);
    if (this.#focusAnimationFrame) cancelAnimationFrame(this.#focusAnimationFrame);
    this.#openAnimationFrame = 0;
    this.#focusAnimationFrame = 0;
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
